import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizePrice } from '@/utils/normalizePrice';
import { WarnRedIcon, SquareWarnIcon } from '@/components/common/icon';
import { styled } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import {
  getPlatformVideoContentType,
  getPlatformVideoContentTypeDesc
} from '@/biz/platform';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { OrderStatus } from '@/interface/order';
import Radio from '@/components/Radio/index';
import { AdsCampaignMarketingStrategy } from '@/interface/campaign';
import { ProductType, ProductPhysicalSelectEnum } from '../config';
import axiosInstance from '../../../apis/axiosInstance';
import { isMobile } from '@/biz/tool';

const CustomTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: '#ccc'
    },
    '&:hover fieldset': {
      borderColor: '#aaa'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
      borderWidth: '1px'
    }
  }
});

const PriceFieldsComponent = ({
  direction = 'flex flex-col',
  hasRadioButton = false,
  orderPriceAdjustment = false,
  newOrderPrice,
  setNewOrderPrice,
  maxOrderPrice
}: {
  direction?: 'flex flex-row' | 'flex flex-col';
  hasRadioButton?: boolean;
  orderPriceAdjustment?: boolean;
  newOrderPrice?: number | string;
  setNewOrderPrice?: (v: number | string) => void;
  maxOrderPrice?: number | null;
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { gbPricingSchemaFlag } = useMultiplePricesStore();

  const { order, campaign } = detailData;
  const priceFields = order?.priceFields;
  const interestedPriceFields = order?.interestedPriceFields;
  const alreadyShownInterest = order?.alreadyShownInterest || 0;
  const [creatorMaxActualGet, setCreatorMaxActualGet] = useState<number | null>(
    null
  );
  const [campaignMaxActualGet, setCampaignMaxActualGet] = useState<
    number | null
  >(null);
  const [
    isPriceHigherThanCampaignMultiplier,
    setIsPriceHigherThanCampaignMultiplier
  ] = useState(false);
  const [isPriceHigherThanCreatorMax, setIsPriceHigherThanCreatorMax] =
    useState(false);
  const { selectedPriceFields, setSelectedPriceFields } =
    useMultiplePricesStore();
  const getApplyForNum = () => {
    let ApplyForNum = 0;
    if (alreadyShownInterest < 20) {
      ApplyForNum = 0;
    }
    if (alreadyShownInterest >= 20 && alreadyShownInterest < 50) {
      ApplyForNum = 20;
    }
    if (alreadyShownInterest >= 50 && alreadyShownInterest < 100) {
      ApplyForNum = 50;
    }
    if (alreadyShownInterest >= 100) {
      ApplyForNum = 100;
    }
    if (ApplyForNum === 0) {
      return null;
    }
    return (
      <div className="w-[762px] mb-4 justify-start text-[#60646C] text-xs font-normal font-['Inter'] leading-none">
        {t('price.fields.interest_count', {
          defaultValue:
            'Over {{countNum}} creators has shown interest in this deal.',
          countNum: ApplyForNum
        })}
      </div>
    );
  };

  // 先请求下改价范围
  const getSetPriceRange = async (selectedData?: any) => {
    const interestedPriceFields = selectedData || selectedPriceFields;
    if (interestedPriceFields?.length === 0) {
      return;
    }
    const params = {
      interestedPriceField: {
        ...interestedPriceFields[0]
      }
    };
    const { data } = await axiosInstance.post(
      `/order/${detailData?.order?.id}/competitivePrice`,
      params
    );
    setCreatorMaxActualGet(
      data?.data?.interestedPriceField?.creatorMaxActualGet
    );
    setCampaignMaxActualGet(
      data?.data?.interestedPriceField?.campaignMaxActualGet
    );
  };
  const newOrderPriceChange = (e: any) => {
    if (setNewOrderPrice) {
      const value = Number(e.target.value);
      if (Number.isNaN(value)) return;

      const maxPrice = maxOrderPrice || creatorMaxActualGet;
      if (maxPrice !== null && value > maxPrice) {
        setIsPriceHigherThanCreatorMax(true);
        setNewOrderPrice(maxPrice);
        return;
      }
      setIsPriceHigherThanCreatorMax(false);

      if (campaignMaxActualGet != null && value > campaignMaxActualGet) {
        setIsPriceHigherThanCampaignMultiplier(true);
        setNewOrderPrice(value);
        return;
      }
      setIsPriceHigherThanCampaignMultiplier(false);
      if (value > 0) {
        setNewOrderPrice(value);
      }
      if (value === 0) {
        setNewOrderPrice('');
      }
    }
  };
  useEffect(() => {
    // 为了达人改价后没有刷新页面的情况下，通弹窗中按钮又回到这个页面，那么selectedPriceFields的套餐价格是上次操作完后改价的内容
    if (
      selectedPriceFields.length > 0 &&
      priceFields &&
      priceFields.length > 0
    ) {
      priceFields.forEach((e: any) => {
        if (e.contentType === selectedPriceFields[0].contentType) {
          setSelectedPriceFields([e]);
        }
      });
    }
    getSetPriceRange();
  }, []);
  useEffect(() => {
    if (
      setNewOrderPrice &&
      newOrderPrice &&
      selectedPriceFields[0]?.actualGet < +newOrderPrice
    ) {
      setNewOrderPrice(0);
      setIsPriceHigherThanCampaignMultiplier(false);
      setIsPriceHigherThanCreatorMax(false);
    }
  }, [selectedPriceFields]);

  if (!gbPricingSchemaFlag) {
    // 暂未拿到 ab 测试结果
    return (
      <div className="w-full flex justify-center items-center gap-8 ">
        {t('price.fields.loading', { defaultValue: 'loading price...' })}
      </div>
    );
  }
  // no.0 Commission rate
  if (
    order?.marketingStrategy ===
    AdsCampaignMarketingStrategy.AFFILIATES_MARKETING
  ) {
    if (!hasRadioButton) {
      return (
        <div className="w-full flex flex-col justify-center items-center gap-8 ">
          <div className="w-full self-stretch px-4 py-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-[#e8e8ec] inline-flex flex-col justify-center items-start gap-2">
            <div className="self-stretch bg-white inline-flex justify-between items-center">
              <div className="w-[186px] justify-start text-[#1c2024] text-sm font-semibold font-['Plus_Jakarta_Sans'] leading-snug">
                {t('price.fields.commission.title', {
                  defaultValue: 'Commission'
                })}
              </div>
              <div className="justify-start text-[#1c2024] text-base font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
                {order?.commissionRate * 100 || '--'}%
              </div>
            </div>
            <div className="self-stretch justify-start text-[#60646c] text-xs font-normal font-['Inter'] leading-[18px]">
              {t('price.fields.commission.desc', {
                defaultValue:
                  'You gain {{rate}}% of the total sales generated from your affiliate link',
                rate: order?.commissionRate * 100 || '--'
              })}
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="self-stretch p-4 rounded-lg outline outline-[1.50px] outline-offset-[-1.50px] outline-[#5b3de9] inline-flex flex-col justify-start items-start gap-2">
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="justify-start text-[#8b8d98] text-xs font-semibold font-['Plus_Jakarta_Sans'] leading-[18px]">
                {t('price.fields.total_payment', {
                  defaultValue: 'Total payment'
                })}
              </div>
              <div className="w-4 h-4 relative rounded-full outline outline-2 outline-offset-[-2px] outline-[#5a31f0] overflow-hidden">
                <div className="w-2 h-2 left-[4px] top-[4px] absolute bg-[#5a31f0] rounded-full" />
              </div>
            </div>
            <div className="justify-start text-[#1c2024] text-base font-semibold font-['Plus_Jakarta_Sans'] leading-normal">
              {t('price.fields.commission.rate', {
                defaultValue: '{{rate}}% commission generated from the sales',
                rate: order?.commissionRate * 100 || '--'
              })}
            </div>
          </div>
          <div className="self-stretch justify-start text-[#60646c] text-xs font-normal font-['Inter'] leading-[18px]">
            {t('price.fields.commission.desc', {
              defaultValue:
                'You gain {{rate}}% of the total sales generated from your affiliate link',
              rate: order?.commissionRate * 100 || '--'
            })}
          </div>
        </div>
      );
    }
  }

  // no.1 老订单
  // old 单一内容类型定价
  if (!priceFields?.length) {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-8 ">
        <PriceFieldItem
          priceData={{
            price: order?.price,
            contentType: getPlatformVideoContentType({
              platform: order?.platform
            })
          }}
          alwaysShowPrice
          hasRadioButton={hasRadioButton}
        />
        {order?.status.includes(OrderStatus.Invited) && (
          <p>
            {t('price.fields.abnormal_order', {
              defaultValue: 'The order is abnormal, please contact'
            })}{' '}
            <a
              className="text-[#0c67e6] cursor-pointer"
              href="mailto:support@creator.aha.inc"
            >
              support@creator.aha.inc
            </a>
            .
          </p>
        )}
      </div>
    );
  }

  // no.2 新订单，已提交价格
  if (interestedPriceFields?.length > 0) {
    return (
      <div className="w-full justify-center items-center gap-4 ">
        {interestedPriceFields?.map((el: any, index: number) => {
          return (
            <React.Fragment key={`${el.contentType}-${index}`}>
              <PriceFieldItem
                priceData={el}
                alwaysShowPrice
                hasRadioButton={hasRadioButton}
              />
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // no.3 新订单，未提交价格，gbPricingSchemaFlag是 not_display_price_can_change
  if (priceFields?.length && !interestedPriceFields?.length) {
    // 加入前 不展示定价
    if (gbPricingSchemaFlag === 'not_display_price_can_change') {
      return (
        <div className="w-full flex justify-center items-center gap-8 ">
          <span>
            {t('price.fields.open_budget', { defaultValue: 'Open budget' })}
          </span>
        </div>
      );
    }
  }

  // no.4 新订单，未提交价格
  return (
    <>
      {hasRadioButton &&
        detailData?.product?.productType === ProductType.PHYSICAL_PRODUCT &&
        detailData?.product?.physicalProductShipType ===
          ProductPhysicalSelectEnum.ship && (
          <div className="px-4 py-2 bg-[#EFF6FF] rounded-lg -mb-3 flex items-start gap-2">
            <SquareWarnIcon className="w-6 h-6" />
            <div
              className={`flex ${isMobile() ? 'flex-col justify-start items-start' : 'items-center'}`}
            >
              <span>
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('price.fields.receiving_product', {
                      defaultValue:
                        "You're receiving a product (worth <b>${{physicalProductCashValue}}</b>)",
                      physicalProductCashValue:
                        detailData?.product?.physicalProductCashValue
                    })
                  }}
                ></span>
                <span>{!(selectedPriceFields[0]?.actualGet > 0) && '.'}</span>
              </span>
              {selectedPriceFields[0]?.actualGet > 0 && (
                <>
                  {!isMobile() && '\u00A0'}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('price.fields.receiving_product_cash', {
                        defaultValue: 'and <b>${{actualGet}}</b> in cash.',
                        actualGet: selectedPriceFields[0]?.actualGet || 0
                      })
                    }}
                  ></span>
                </>
              )}
            </div>
          </div>
        )}
      {/*  */}
      <div
        className={`w-full justify-center items-stretch gap-4
        ${direction}
        `}
      >
        {priceFields?.map((el: any, index: number) => {
          return (
            <React.Fragment key={`${el.contentType}-${index}`}>
              <PriceFieldItem
                priceData={el}
                alwaysShowPrice
                hasRadioButton={hasRadioButton}
                getSetPriceRange={getSetPriceRange}
              />
            </React.Fragment>
          );
        })}
      </div>
      {orderPriceAdjustment && (
        <div className="bg-[#FAFAF9] p-4 rounded-lg">
          <div className="mb-1 justify-start text-text-color-primary text-base font-semibold font-['Plus_Jakarta_Sans'] leading-snug">
            {t('price.fields.competitive_price.title', {
              defaultValue: 'Want to submit a more competitive or higher price?'
            })}
          </div>
          <div className="mb-4 self-stretch justify-start text-[#57534D] text-xs font-normal font-['DM_Sans'] leading-normal">
            {t('price.fields.competitive_price.desc', {
              defaultValue:
                'Competitive prices will be prioritized in brand visibility and increase your chance of getting selected. If you prefer a higher price, please ensure it reflects the true value of your content — excessive pricing may reduce selection likelihood.'
            })}
          </div>
          {getApplyForNum()}
          <div>
            <div className="mb-2 justify-start text-[#60646C] text-xs font-medium font-['Inter'] leading-tight">
              {t('price.fields.competitive_price.enter_new', {
                defaultValue: 'Enter a new price'
              })}
            </div>
            <CustomTextField
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                )
              }}
              type="number"
              placeholder={t('price.fields.competitive_price.placeholder', {
                defaultValue: '0',
                price: selectedPriceFields[0]?.actualGet
              })}
              value={newOrderPrice}
              onChange={(e) => newOrderPriceChange(e)}
              style={{ width: '320px', borderRadius: '8px' }}
            />
            {isPriceHigherThanCampaignMultiplier &&
              !isPriceHigherThanCreatorMax && (
                <span className="w-full mt-4 py-1 flex rounded">
                  <WarnRedIcon className="w-[36px] h-[18px] mr-1" />
                  <div className="self-stretch justify-start text-[#E7000B] text-sm font-normal font-['DM_Sans'] leading-snug">
                    {t(
                      'price.fields.competitive_price.maxCampaignMultiplierWarning',
                      {
                        defaultValue:
                          'Your proposed price is higher than most other influencers. Offering a more competitive rate will significantly increase your chances of being approved by the brand.'
                      }
                    )}
                  </div>
                </span>
              )}
            {isPriceHigherThanCreatorMax && (
              <span className="w-full mt-4 py-1 flex rounded">
                <WarnRedIcon className="w-[36px] h-[18px] mr-1" />
                <div
                  className="self-stretch justify-start text-[#E7000B] text-sm font-normal font-['DM_Sans'] leading-snug"
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'price.fields.competitive_price.maxCreatorMaxWarning',
                      {
                        defaultValue:
                          'Your proposed price is above the deal’s limit (<b>{{max_amount}} USD</b>). Submitting a more competitive price will improve your chances of being selected by the brand.',
                        max_amount: maxOrderPrice || creatorMaxActualGet
                      }
                    )
                  }}
                ></div>
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const PriceFieldItem = ({
  priceData,
  alwaysShowPrice,
  hasRadioButton = false,
  getSetPriceRange
}: {
  priceData: any;
  alwaysShowPrice?: boolean;
  hasRadioButton?: boolean;
  getSetPriceRange?: (selectedData?: any) => void;
}) => {
  const { t } = useTranslation();
  const { detailData } = useDetailDataStore();
  const { order, product } = detailData;
  const interestedPriceFields = order?.interestedPriceFields;

  const { selectedPriceFields, setSelectedPriceFields, gbPricingSchemaFlag } =
    useMultiplePricesStore();
  const { contentType, price, originPrice, actualGet } = priceData;
  let showPrice = actualGet;
  // if (order?.status.includes(OrderStatus.Invited)) {
  //   // Invited 的时候 可以更改 price字段。
  //   // 且 只有 Invited 的时候 优先展示 originPrice 字段
  //   showPrice = originPrice;
  // }

  return (
    <div
      className={`gap-1 flex flex-col justify-start items-start border border-solid rounded-lg px-4 py-3 w-full
      ${
        selectedPriceFields?.find((el) => el?.contentType === contentType) ||
        interestedPriceFields?.find(
          (el: any) => el?.contentType === contentType
        )
          ? 'border-theme-color'
          : 'border-[#e8e8ec]'
      }
          ${
            interestedPriceFields?.length > 0 ||
            !order?.status.includes(OrderStatus.Invited)
              ? 'cursor-default'
              : 'cursor-pointer'
          }
      `}
      onClick={() => {
        if (
          interestedPriceFields?.length > 0 ||
          !order?.status.includes(OrderStatus.Invited)
        ) {
          return;
        }
        setSelectedPriceFields([priceData]);
        getSetPriceRange?.([priceData]);
      }}
    >
      <div className="flex justify-between w-full">
        <div
          className={`flex justify-between w-full flex-col items-start gap-2
        `}
        >
          <div
            className={`text-[#8B8D98] text-[12px] font-['Plus Jakarta Sans'] leading-snug
            ${hasRadioButton ? 'text-[#8B8D98]' : ''}
            `}
          >
            {getPlatformVideoContentType({
              platform: order?.platform,
              contentType: contentType
            })}
          </div>
          {/* 下面两个显示价格 */}
          {gbPricingSchemaFlag === 'display_price_cannot_change' ||
          gbPricingSchemaFlag === 'display_price_can_change' ||
          alwaysShowPrice ? (
            <div className="text-[#1c2024] text-base font-semibold font-['Plus Jakarta Sans'] leading-normal">
              {showPrice === 0 &&
              product?.productType === ProductType.PHYSICAL_PRODUCT &&
              product?.physicalProductShipType ===
                ProductPhysicalSelectEnum.ship
                ? t('price.fields.free_product', {
                    defaultValue: 'Free product'
                  })
                : !isNaN(showPrice)
                  ? normalizePrice(showPrice)
                  : ''}
            </div>
          ) : null}
        </div>
        {hasRadioButton && (
          <Radio
            checked={Boolean(
              selectedPriceFields?.find((el) => el?.contentType === contentType)
            )}
          />
        )}
      </div>
      <div className="text-[#60646c] text-xs font-normal font-['Inter'] leading-[18px]">
        {getPlatformVideoContentTypeDesc(
          getPlatformVideoContentType({
            platform: order?.platform,
            contentType: contentType
          })
        )}
      </div>
    </div>
  );
};

export default PriceFieldsComponent;
